import './reset.css';

import {ThemeContainer, ThemeProvider} from '@dropbox/dig-foundations';
import * as Sentry from '@sentry/react';
import {CurrentEnvironment, getCurrentEnvironment, isStage} from 'helpers/environment';
import {shouldAlwaysSendLexicalPasteError} from 'helpers/lexicalPasteErrors';
import {classifySentryError} from 'helpers/logging';
import {installSessionExpiryHandler} from 'helpers/sessionExpiryHandler';
import {installStaleChunkReloadHandler} from 'helpers/staleChunkReloadHandler';
import {useAtomValue} from 'jotai';
import {initializePrivacyModule} from 'lib/initializePrivacyModule';
import React, {StrictMode} from 'react';
import {createRoot} from 'react-dom/client';
import {createRoutesFromChildren, matchRoutes, useLocation, useNavigationType} from 'react-router';

import {loadTTVCTracker} from './analytics/ttvc';
import App from './App';
import {derivedThemeAtom} from './atoms/layout';
import {useSetThemeWatcher} from './hooks/useSetThemeWatcher';
import reportWebVitals from './reportWebVitals';

const currentEnv = getCurrentEnvironment();
const sentryEnv =
  currentEnv === CurrentEnvironment.Production
    ? 'production'
    : currentEnv === CurrentEnvironment.Staging
      ? 'staging'
      : 'development';

// Meticulous: record the current environment once at module load time so that
// replay diffs can be filtered by environment (dev vs staging vs production preview).
const envNames: Record<CurrentEnvironment, string> = {
  [CurrentEnvironment.Development]: 'development',
  [CurrentEnvironment.Amplify]: 'amplify',
  [CurrentEnvironment.Staging]: 'staging',
  [CurrentEnvironment.Production]: 'production',
};
window.Meticulous?.context.recordCustomContext('environment', envNames[currentEnv]);

// Matches any local dev server, including worktree hosts like `abc123.toyon.localhost:59669`.
const localhostUrlPattern =
  /^https?:\/\/(?:(?:[^/?#:]+\.)?localhost|127\.0\.0\.1)(?::\d+)?(?:[/?#]|$)/;

Sentry.init({
  dsn: 'https://cc82b415906e5c0794d9829702e78b7d@d.dropbox.com/4507113446506496',
  release: import.meta.env.VITE_GIT_SHA,
  sampleRate: 1.0,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  environment: sentryEnv,
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost', 'https://*.dropbox.com', 'https://*.dropboxpulse.com'],
  denyUrls: [localhostUrlPattern],
  // Session Replay
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 1.0, // Capture 100% of the errors
  // Filter or reclassify errors based on rules in helpers/logging.ts
  beforeSend(event, hint) {
    const error = hint?.originalException;
    const requestUrl = event.request?.url;

    if (typeof requestUrl === 'string' && localhostUrlPattern.test(requestUrl)) {
      return null;
    }

    // Error page events always go through
    if (event.tags?.source === 'error_page') {
      return event;
    }

    const action = classifySentryError(error);

    if (action === 'drop') {
      return null;
    }

    if (action === 'warning') {
      const message = error instanceof Error ? error.message : String(error);
      Sentry.captureMessage(message, {
        level: 'warning',
        tags: event.tags,
        extra: {url: event.request?.url, ...event.extra},
      });
      return null;
    }

    // Temporarily retain all correlated Lexical #66/#211 paste failures so the
    // diagnostic breadcrumbs are not lost to the general error sample.
    if (shouldAlwaysSendLexicalPasteError(event, error)) {
      return event;
    }

    // Manual 10% sampling for other non-error-page errors (replaces sampleRate: 0.1)
    if (Math.random() > 0.1) {
      return null;
    }

    return event;
  },
  // Also filter breadcrumbs for cleaner traces
  beforeBreadcrumb(breadcrumb) {
    // Filter out cancelled XHR requests from breadcrumbs
    if (breadcrumb.category === 'xhr' && breadcrumb.data?.status_code === 0) {
      return null;
    }
    return breadcrumb;
  },
});

installSessionExpiryHandler();
installStaleChunkReloadHandler();

loadTTVCTracker();

const AppWrapper = ({children}: {children: React.ReactNode}) => {
  useSetThemeWatcher();
  const theme = useAtomValue(derivedThemeAtom);

  React.useEffect(() => {
    window.Meticulous?.context.recordCustomContext('theme', theme);
  }, [theme]);

  return (
    <ThemeProvider mode={theme} theme="vis2023">
      <ThemeContainer rootElement={document.documentElement}>{children}</ThemeContainer>
    </ThemeProvider>
  );
};

const Applicaton = () => {
  return (
    <StrictMode>
      <AppWrapper>
        <App />
      </AppWrapper>
    </StrictMode>
  );
};

const container = document.getElementById('root');
const root = createRoot(container!);
root.render(<Applicaton />);

// Disable privacy module on staging
if (!isStage) {
  initializePrivacyModule();
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
